import { queryOptions, useQuery } from '@tanstack/react-query';
import {
  fetchTracingDepthCompletion,
  fetchTracingPoDocuments,
} from '@/api/report';
import { useSessionStore } from '@/store/sessionStore';

export function tracingDepthCompletionQueryOptions(
  companyId: string,
  filter: string,
) {
  return queryOptions({
    queryKey: [
      'report',
      'tracing-depth-completion',
      companyId,
      filter,
    ] as const,
    queryFn: () => fetchTracingDepthCompletion(companyId, filter),
    enabled: !!companyId && !!filter,
  });
}

export function useTracingDepthCompletionQuery(filter: string) {
  const companyId = useSessionStore(s => s.user?.companyId ?? '');
  return useQuery(tracingDepthCompletionQueryOptions(companyId, filter));
}

export function useTracingPoDocumentsQuery(params: {
  filter: string;
  isMissing: boolean;
  page: number;
  count: number;
  sort?: string;
}) {
  const companyId = useSessionStore(s => s.user?.companyId ?? '');
  return useQuery({
    queryKey: [
      'report',
      'tracing-po-documents',
      companyId,
      params.filter,
      params.isMissing,
      params.page,
      params.count,
      params.sort ?? '',
    ] as const,
    queryFn: () => {
      const qs = new URLSearchParams({
        filter: params.filter,
        isMissing: String(params.isMissing),
        page: String(params.page),
        count: String(params.count),
        ...(params.sort ? { sort: params.sort } : {}),
      });
      return fetchTracingPoDocuments(companyId, qs.toString());
    },
    select: res => ({
      ...res,
      pagination: {
        total:
          res.meta?.totalCount ?? res.meta?.total ?? res.pagination?.total ?? 0,
        page:
          (res.meta?.page !== undefined ? res.meta.page + 1 : null) ??
          res.pagination?.page ??
          1,
        count: res.meta?.count ?? res.pagination?.count ?? 20,
      },
    }),
    enabled: !!companyId && !!params.filter,
    placeholderData: prev => prev,
  });
}
